<template>
  <div data-allow-mismatch="children">
    <component
      :is="isLink ? AppLink : 'button'"
      v-bind="{ to: authPageLink }"
      class="c-btn relative inline-flex h-10 w-10 items-center justify-center border-gray-250 bg-gray-100 p-0 font-semibold text-blue-700 hover:bg-gray-250 focus-visible:border-blue-700 active:border-gray-300 active:bg-gray-100 sm:h-auto sm:w-auto sm:min-w-20 sm:gap-1.5 sm:px-4 sm:py-2"
      :aria-label="label"
    >
      <UserIcon
        class="h-6 w-6 shrink-0 fill-current transition-cheap duration-150 ease-out-sine sm:-ml-1"
        :class="{ 'sm:opacity-0': authStore.authStatus === 'loading' }"
      />

      <template v-if="authStore.authStatus !== 'loading'">
        <span
          v-if="authStore.user"
          ref="userName"
          class="hidden truncate leading-6 sm:inline"
          >{{
            authStore.user.display ||
            authStore.user.email ||
            authStore.user.username
          }}</span
        >
        <IntlMsg
          v-else
          class="hidden truncate sm:inline"
          v-bind="{ k: 'auth.login.title' }"
        />
      </template>
      <span
        class="pointer-events-none absolute -mx-2 hidden h-4 w-12 rounded-full bg-gray-300 transition-cheap duration-150 ease-out-sine sm:inline-block"
        :class="{
          'scale-0 opacity-0': authStore.authStatus !== 'loading',
        }"
      ></span>
    </component>
  </div>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router'
import { shallowRef, computed, useTemplateRef, watch, nextTick } from 'vue'
import { UserIcon } from '@heroicons/vue/20/solid'

import { isClient, useScreens, IntlMsg } from '/@shared/app'
import { AppLink } from '/@app'
import { SUBDOMAIN_REDIRECTS } from '/@shared/constants'

import { useAuthStore } from '/@/stores'
import { useI18n } from '/@/i18n'

const props = defineProps<{ isRuUser?: boolean }>()

const authStore = useAuthStore()
const { t } = useI18n()

const isLoaded = shallowRef(false)
if (isClient) isLoaded.value = true

const label = computed(() => {
  if (authStore.user)
    return (
      authStore.user.display || authStore.user.email || authStore.user.username
    )

  return t('auth.login.title')
})

const isLink = computed(() => {
  if (authStore.user) return false
  if (isLoaded.value && (!props.isRuUser || !SUBDOMAIN_REDIRECTS)) return false

  return true
})

const authPageLink = computed<RouteLocationRaw | undefined>(() => {
  if (isLoaded.value && (!props.isRuUser || !SUBDOMAIN_REDIRECTS))
    return undefined
  return authStore.user ? '/my/' : { name: 'LoginPage' }
})

const userNameRef = useTemplateRef('userName')
const { isSmallScreen } = useScreens()
const stopReveal = watch(
  [userNameRef, isSmallScreen],
  async val => {
    const [el, isSm] = val
    if (!el) return
    if (isSm) return

    const destWidth = Math.min(el.clientWidth, 128)

    const animation = el.animate(
      [
        {
          width: 0,
        },
        {
          width: destWidth + 'px',
        },
      ],
      {
        duration: 150,
        easing: 'cubic-bezier(0.33, 1, 0.68, 1)', // easeOutCubic
        fill: 'forwards',
      }
    )

    await animation.finished
    await nextTick()
    stopReveal()
  },
  { immediate: true }
)
</script>
